<template>
  <NuxtLoadingIndicator color="var(--primary-2)" />
  <Html :dir="head.htmlAttrs.dir">
    <div class="supplier-one-new" :class="{ intro }">
      <!-- `.supplier-root`, not this element, is Lenis's wrapper: Lenis mutates its
           classList directly (`lenis`/`lenis-smooth`), and this element's own `class`
           is Vue-reactive (`:class="{ intro }"`) — Vue's patcher overwrites the whole
           `class` attribute on every re-render this binding triggers (e.g. the intro
           flag flipping shortly after mount), silently wiping out whatever Lenis just
           added. `.supplier-root` carries a static, Vue-untouched class instead. -->
      <div class="supplier-root">
        <!-- Plain, unstyled wrapper around everything that can grow the page's
             height. `.supplier-root` itself never resizes — it's the fixed 100vh
             scroll container above — so its own box can't signal content growth, and
             this div's auto height lets it stand in for "the whole page" for Lenis
             and the ResizeObserver below instead. Plain and unstyled on purpose (no
             transform/filter/contain), so it doesn't become a containing block for
             the `position:fixed` whatsapp button inside it. See the matching comment
             in useSupplierMotion.ts's initSupplierLenis(). -->
        <div class="supplier-content">
          <LayoutTheHeaderTwoEight :route-path="routePath" @open-login="openLogin" />
          <Auth ref="authRef">
            <template #googleSignin>
              <google />
            </template>
          </Auth>
          <!-- Keyed on the route so moving between pages cross-fades rather than
               cutting; `out-in` keeps the two pages from overlapping mid-scroll. -->
          <Transition name="page-fade" mode="out-in">
            <main :key="routePath">
              <slot />
            </main>
          </Transition>
          <LazyLayoutTheFooterTwoEight />
          <slot name="whats-app">
            <LazyLayoutWhatsappFloatButtonPicker :raised="isProductPage" />
          </slot>
        </div>
      </div>
    </div>
  </Html>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted } from "vue";
import ScrollTrigger from "gsap/ScrollTrigger";
import { usePageIntro } from "../composables/usePageIntro";
import { setScrollToHandler } from "../composables/helper";
import {
  destroySupplierLenis,
  getSupplierLenis,
  initSupplierLenis,
  prefersReducedMotion,
  scrollToTarget,
  scrollerEl,
  trigger,
  useGsap,
} from "../composables/useSupplierMotion";

const authRef = ref<any>(null);
function openLogin() {
  authRef.value.open();
}
provide("openLogin", openLogin);
const head = useI18nHead();

const route = useRoute();
const routePath = ref(route.path);
const isProductPage = useIsProductPage();

const { intro } = usePageIntro();

watch(
  () => route.path,
  () => {
    routePath.value = route.path;
  },
);

/*
 * Reset the scroll position on navigation.
 *
 * `plugins/scrollToTop.js` resets `document.body.scrollTop` on every
 * `page:finish`, which is a no-op once Lenis moves the scroll role onto
 * `.supplier-root` below — so opening a page from halfway down the
 * previous one would otherwise drop the visitor into the middle of it. Lenis
 * has to be told too: it tracks its own target offset and would otherwise
 * ease the page straight back to where it was on the next frame.
 */
watch(
  () => route.fullPath,
  () => {
    nextTick(() => {
      getSupplierLenis()?.scrollTo(0, { immediate: true });
      const scroller = scrollerEl() as HTMLElement | null;
      if (scroller) scroller.scrollTop = 0;
      ScrollTrigger.refresh();
    });
  },
);

let ctx: gsap.Context | null = null;
let onResize: (() => void) | null = null;
let contentObserver: ResizeObserver | null = null;

onMounted(() => {
  // Hands the scroll-container role from <body> to `.supplier-root` — must
  // happen before initSupplierLenis() measures its wrapper.
  document.body.classList.add("supplier-body");

  const gsap = useGsap();
  initSupplierLenis();

  // Point the shared scrollToElement() helper at this layout's scroller — its
  // default target (<body>) doesn't scroll here. See setScrollToHandler().
  setScrollToHandler((element, offset) => scrollToTarget(element, offset));

  // Keep ScrollTrigger measurements accurate as below-the-fold content (the
  // API-fed product grid, the lazy footer, images) loads in after mount —
  // `.supplier-content` (not `.supplier-one-new`, whose box is a fixed 100vh
  // that never resizes) is what reliably changes size when the real page
  // content does. See the matching comment in useSupplierMotion.ts.
  onResize = () => ScrollTrigger.refresh();
  window.addEventListener("resize", onResize);
  window.addEventListener("load", onResize);

  const content = document.querySelector<HTMLElement>(".supplier-content");
  if (content && typeof ResizeObserver !== "undefined") {
    let queued = 0;
    contentObserver = new ResizeObserver(() => {
      // Coalesce to one refresh per frame: a refresh can itself nudge layout,
      // and several children usually resize together.
      if (queued) return;
      queued = requestAnimationFrame(() => {
        queued = 0;
        ScrollTrigger.refresh();
        getSupplierLenis()?.resize();
      });
    });
    contentObserver.observe(content);
  }

  if (prefersReducedMotion()) return;

  ctx = gsap.context(() => {
    // Footer "listed on" rows (the trust-listing equivalent of safari's
    // footer partner logos) tick in one after another, phone-only — mirrors
    // the safari template's own mobile-motion-layer scoping.
    const phone = window.matchMedia("(max-width: 640px)");
    if (!phone.matches) return;

    gsap.fromTo(
      ".listing-item",
      { y: 20, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.5,
        ease: "power2.out",
        stagger: 0.08,
        scrollTrigger: trigger({ trigger: ".trust-listing", start: "top 92%" }),
      },
    );
  });
});

onBeforeUnmount(() => {
  if (onResize) {
    window.removeEventListener("resize", onResize);
    window.removeEventListener("load", onResize);
    onResize = null;
  }
  contentObserver?.disconnect();
  contentObserver = null;
  setScrollToHandler(null);
  ctx?.revert();
  ctx = null;
  destroySupplierLenis();
  document.body.classList.remove("supplier-body");
});
</script>

<style lang="scss" scoped>
/* First paint: the bar drops in and the page lifts under it. Everything below
   the fold is handled by each section's own scroll reveal.
 *
 * Animations rather than transitions, applied by a class the client adds at
 * mount: the start state rides along in the keyframes (`both`), so nothing is
 * hidden in the server-rendered markup and a page that never hydrates is
 * still fully visible. */
/* The content column every section centres itself in. Declared once here and
   inherited: sections stay full-bleed, their contents stop at this width. */
.supplier-one-new {
  --shell-max: 1280px;
}
.supplier-root {
  /* This element, not <body>, is the real scroll container while this layout
     is mounted (see the `body.supplier-body` rule below and
     initSupplierLenis() in useSupplierMotion.ts). Lenis hard-codes a rule
     that silently coerces a `wrapper` of `document.body` or
     `document.documentElement` back to `window` — there's no way to point it
     at body directly — so smooth scroll needs its own container one level
     down. `position:fixed` descendants (the WhatsApp button) stay correct
     because this rule intentionally avoids `transform`/`filter`/`contain`, so
     it never becomes their containing block. Not `.supplier-one-new` itself:
     see the template comment above `.supplier-root` for why Lenis needs an
     element Vue's own `:class` binding never repaints. */
  height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.supplier-one-new.intro {
  /* The bar's own box must stay transform-free: the mobile menu sheet is a
     `position: fixed` descendant of it, and a transformed ancestor becomes
     that sheet's containing block — which pinned it inside the 70px bar and
     put the page on top of it. The inner row moves instead.
     `backwards`, not `both`, for the same reason: `both` leaves an identity
     matrix behind after the animation ends, which still counts as a
     transform. */
  :deep(.header-two-eight-inner) {
    animation: bar-drop 0.6s cubic-bezier(0.22, 1, 0.36, 1) backwards;
  }
  main {
    animation: page-rise 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.08s backwards;
  }
}
@keyframes bar-drop {
  from {
    opacity: 0;
    transform: translateY(-12px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
@keyframes page-rise {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* Route changes. The leave is quicker than the enter so navigation feels
   answered rather than drawn out. */
.page-fade-enter-active {
  transition:
    opacity 0.45s ease,
    transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}
.page-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}
.page-fade-enter-from {
  opacity: 0;
  transform: translateY(12px);
}
.page-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (prefers-reduced-motion: reduce) {
  .supplier-one-new.intro :deep(.header-two-eight-inner),
  .supplier-one-new.intro main {
    animation: none;
  }
  .page-fade-enter-active,
  .page-fade-leave-active {
    transition: none;
  }
}
</style>

<style lang="scss">
/* Unscoped: `body` is outside this component's render tree, so a scoped rule
   (which only ever matches elements this template renders) could never reach
   it. Hands the scroll-container role to `.supplier-root` above — see
   initSupplierLenis() in useSupplierMotion.ts. */
body.supplier-body {
  overflow: hidden;
}
</style>
